import { useCallback, useEffect, useState, useMemo, useRef, forwardRef, useImperativeHandle } from "react";
import styled from "styled-components";
import {
  TIMELINE_CONFIG,
} from "../../constants";
import { useSelector } from "react-redux";
import BackdropPortal from "../../components/shared/BackdropPortal";
import { withApollo } from "react-apollo";
import CalendarMainPage from "../book-equipment/calendar-timeline/CalendarMainPage";
import { CustomFilterContext } from "../booking-instruments/booking-instruments-custom-filter/context";
import { loadBookingInstruments as loadBookingInstrumentsAction } from "../booking-instruments/redux/actions";
import { loadPendingReservations as loadPendingReservationsAction } from "../booking-calendars/redux/actions";
import { connect } from "react-redux";
import { Formik } from "formik";
import useInstrumentCache from "../../utils/hooks/useInstrumentCache";
import { useSharedFilterState } from "./SharedFilterContext";
import { debounce } from "lodash";
import { bookingOperationsService } from "../booking-operations/bookingOperationsService";
import { quickEditTracker } from "../booking-operations/quickEditTracker";
import { ALL_EQUIPMENT } from "../../constants";
import { RecurringBookingProvider } from "../book-equipment/calendar-timeline/RecurringBooking/Context/RecurringBookingContext";
import "./EquipmentBooking.scss";

const MainViewStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-grow: 1;
`;

const EquipmentBookingGrid = ({
  viewData,
  setViewData,
  client,
  eventID = null,
  isEquipmentView,
  isTimelineView,
  loadBookingInstruments,
  loadPendingReservations,
  setPopupEnableStatus,
  savedFilter,
  setSavedFilter,
  signeeFilter,
  selectedDateRange,
  isGroupView = false,
  onOperationStart = () => { },
  onOperationComplete = () => { },
  forwardedRef,
  listGroupByView,
  setListGroupByView
}) => {
  const user = useSelector((store) => store.user);
  const [selectedStartDate, setSelectedStartDate] = useState(selectedDateRange?.[0] ? new Date(selectedDateRange?.[0]) : new Date());
  const [selectedEndDate, setSelectedEndDate] = useState(selectedDateRange?.[1] ? new Date(selectedDateRange?.[1]) : new Date());
  const [view, setView] = useState("week");
  const [calView, setCalView] = useState(TIMELINE_CONFIG.WEEK);
  const [tabList, setTabList] = useState([
    { id: 1, isActive: false, isDisable: false, heading: "Equipment", content: "" },
    { id: 2, isActive: true, isDisable: false, heading: "Calendar", content: "" },
    { id: 3, isActive: false, isDisable: true, heading: "Summary", content: "" },
  ]);
  const [newEquipmentIds, setNewEquipmentIds] = useState([]);
  const [cancelStatus, setCancelStatus] = useState(false);
  const [deleteResourceId, setDeleteResourceId] = useState(null);

  const [selectedList, setSelectedList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Refs for managing async operations and state
  const abortControllerRef = useRef(null);
  const mountedRef = useRef(true);
  const lastLoadedParamsRef = useRef(null);
  const selectedListRef = useRef(selectedList);

  useEffect(() => {
    selectedListRef.current = selectedList;
  }, [selectedList]);

  // Use shared filter state
  const {
    filterState,
    isFilterDataReady,
    loadFilterData,
    updateCurrentFilter
  } = useSharedFilterState();

  const instrumentCache = useInstrumentCache({
    client,
    user,
    masterDataFilters: filterState.masterDataFilters
  });

  // Load filter data on mount
  useEffect(() => {
    if (!isFilterDataReady && user?.site && user?.id) {
      loadFilterData();
    }
  }, [loadFilterData, isFilterDataReady, user?.site, user?.id]);

  // Update current filter when savedFilter changes
  useEffect(() => {
    if (isFilterDataReady) {
      updateCurrentFilter(savedFilter);
    }
  }, [savedFilter, isFilterDataReady, updateCurrentFilter]);

  // Optimized equipment data loading function with proper cleanup
  const loadEquipmentData = useCallback(async (activeFilter, signee, force = false) => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Check if we need to load (avoid duplicate requests)
    const currentParams = {
      filterName: activeFilter?.filterName || null,
      signeeUserId: signee?.userId || null,
      isEquipmentView: isTimelineView
    };

    if (!force && lastLoadedParamsRef.current &&
      JSON.stringify(lastLoadedParamsRef.current) === JSON.stringify(currentParams)) {
      return;
    }

    setLoading(true);

    try {
      if (signal.aborted) return;

      let bookingInstruments = [];
      let allEquipmentIds = [];

      if (!activeFilter || activeFilter.filterName === "" || activeFilter.filterName === ALL_EQUIPMENT || !activeFilter.filterDefinition) {
        // No filter or ALL_EQUIPMENT case - load all bookable equipment from CloudSearch
        const { items: allInstruments } = await instrumentCache.loadInstruments(
          user.site,
          {},
          { signal }
        );
        if (signal.aborted) return;

        // Convert CloudSearch results to expected format
        bookingInstruments = allInstruments.map(instrument => ({
          ...instrument,
          shortIdentifier: instrument.equipmentNickName !== "null" && instrument.equipmentNickName
            ? instrument.equipmentNickName
            : instrument.equipmentModel || instrument.shortIdentifier || instrument.inventoryId,
        }));

        bookingInstruments.sort((a, b) => {
          const aIdentifier = a.shortIdentifier || '';
          const bIdentifier = b.shortIdentifier || '';
          return aIdentifier.localeCompare(bIdentifier);
        });

        allEquipmentIds = bookingInstruments.map(eq => eq.inventoryId);
      } else {
        // Use CloudSearch with filter - show ALL matching equipment
        const { items: filteredInstruments } = await instrumentCache.loadInstruments(
          user.site,
          activeFilter.filterDefinition,
          { signal }
        );

        if (signal.aborted) return;

        // Convert CloudSearch results to expected format
        bookingInstruments = filteredInstruments.map(instrument => ({
          ...instrument,
          shortIdentifier: instrument.equipmentNickName !== "null" && instrument.equipmentNickName
            ? instrument.equipmentNickName
            : instrument.equipmentModel || instrument.shortIdentifier || instrument.inventoryId,
        }));

        bookingInstruments.sort((a, b) => {
          const aIdentifier = a.shortIdentifier || '';
          const bIdentifier = b.shortIdentifier || '';
          return aIdentifier.localeCompare(bIdentifier);
        });

        allEquipmentIds = bookingInstruments.map(eq => eq.inventoryId);
      }

      if (signal.aborted || !mountedRef.current) return;

      // Store the equipment data for the calendar timeline
      loadBookingInstruments({ bookingInstruments });
      if (allEquipmentIds.length > 0 || selectedListRef.current.length === 0) {
        setSelectedList(allEquipmentIds);
      }
      lastLoadedParamsRef.current = currentParams;

    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error("🚨 EquipmentBookingGrid: Error loading equipment data:", error);
      if (mountedRef.current) {
        setSelectedList([]);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.site, instrumentCache, loadBookingInstruments, view, isTimelineView]);

  // Debounced loader
  const debouncedLoad = useRef(
    debounce((filter, signee) => loadEquipmentData(filter, signee), 400)
  ).current;

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      debouncedLoad.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedLoad]);

  // Simplified effect for handling filter changes and equipment view
  useEffect(() => {
    if (!isTimelineView || !isFilterDataReady) {
      return;
    }
    debouncedLoad(filterState.currentFilter, signeeFilter);
  }, [isTimelineView, isFilterDataReady, filterState.currentFilter, signeeFilter, debouncedLoad]);

  const getEquipmentDetailsList = useCallback((inventoryIdList, detailList) => {
    const bookingInstruments = [];
    inventoryIdList.forEach((x) => {
      const findRow = detailList.find((y) => y.id === x);
      if (findRow !== undefined) {
        bookingInstruments.push({
          ...findRow,
          inventoryId: findRow.id,
          shortIdentifier: findRow?.shortIdentifier ?? findRow.equipmentModel,
        });
      }
    });
    return bookingInstruments;
  }, []);

  const calendarMainPageProps = useMemo(() => ({
    client,
    loadBookingInstruments,
    loadPendingReservations,
    getEquipmentDetailsList,
    selectedStartDate,
    setSelectedStartDate,
    selectedEndDate,
    setSelectedEndDate,
    originalSelectedStartDate: selectedStartDate,
    originalSelectedEndDate: selectedEndDate,
    tabList,
    setTabList,
    view,
    setView,
    calView,
    setCalView,
    newEquipmentIds,
    setNewEquipmentIds,
    cancelStatus,
    setCancelStatus,
    deleteResourceId,
    setDeleteResourceId,
    isEquipmentView,
    isTimelineView,
    user,
    setPopupEnableStatus,
    visibleDatePicker: false,
    signeeFilter,
    savedFilter,
    isGroupView,
    listGroupByView,
    setListGroupByView
  }), [
    client, loadBookingInstruments, loadPendingReservations, getEquipmentDetailsList,
    selectedStartDate, selectedEndDate, tabList, view, calView, newEquipmentIds,
    cancelStatus, deleteResourceId, isEquipmentView, user, setPopupEnableStatus,
    signeeFilter, savedFilter, isGroupView, listGroupByView, isTimelineView, setListGroupByView
  ]);

  // Expose methods to parent via ref
  useImperativeHandle(forwardedRef, () => ({
    handleSubscriptionEvent: async (data, operation) => {
      const subscriptionData = data?.data?.onUpdateBookingCommand;

      if (!subscriptionData) {
        return;
      }

      const commandId = subscriptionData.id;

      if (commandId && quickEditTracker.isQuickEdit(commandId)) {
        const quickEditOperation = quickEditTracker.getOperation(commandId);

        bookingOperationsService.handleResponse(data, {
          operation: quickEditOperation,
          skipNotification: false
        }, {});

        // Trigger equipment data reload
        if (filterState.currentFilter) {
          await loadEquipmentData(filterState.currentFilter, signeeFilter, true);
        }

        onOperationComplete?.({ type: quickEditOperation, operation: quickEditOperation, success: true, data });
        return;
      }

      bookingOperationsService.handleResponse(data, {
        operation,
        skipNotification: false
      }, {});

      // Trigger equipment data reload
      if (filterState.currentFilter) {
        await loadEquipmentData(filterState.currentFilter, signeeFilter, true);
      }

      onOperationComplete?.({ type: operation, operation, success: true, data });
    },

    refreshCalendar: async (_options = {}) => {
      if (filterState.currentFilter) {
        await loadEquipmentData(filterState.currentFilter, signeeFilter, true);
      }
    },

    getState: () => ({
      isLoading: loading,
      selectedList
    })
  }), [filterState.currentFilter, signeeFilter, loadEquipmentData, loading, selectedList, onOperationComplete]);

  if (loading) {
    return (
      <MainViewStyled>
        <BackdropPortal show={true} spinnerSize="large" />
      </MainViewStyled>
    );
  }

  return (
    <MainViewStyled>
      <CustomFilterContext.Provider value={{ selectedList, setSelectedList }}>
        <Formik initialValues={{}} validateOnMount={true}>
          <RecurringBookingProvider originalSelectedStartDate={selectedStartDate} originalSelectedEndDate={selectedEndDate}>
            <CalendarMainPage {...calendarMainPageProps} />
          </RecurringBookingProvider>
        </Formik>
      </CustomFilterContext.Provider>
    </MainViewStyled>
  );
};

const EquipmentBookingGridWithApollo = withApollo(EquipmentBookingGrid);

const EquipmentBookingGridWithRef = forwardRef((props, ref) => (
  <EquipmentBookingGridWithApollo {...props} forwardedRef={ref} />
));

export default connect(null, {
  loadBookingInstruments: loadBookingInstrumentsAction,
  loadPendingReservations: loadPendingReservationsAction,
}, null, { forwardRef: true })(EquipmentBookingGridWithRef);
